import { apiClient } from '@/shared/api';
import { useAuthStore } from '@/shared/model';
import type { CreatePageView } from '@aegislab/shared';

/**
 * 페이지 사용량 측정.
 * raw URL 대신 TanStack 라우트 패턴(routeId, 예: '/quest/$questId')을 보낸다 —
 * URL 에 UUID 가 박히면 path 카디널리티가 폭발해 집계가 무의미해진다.
 * userId 는 보내지 않는다. 서버가 쿠키 세션에서 뽑는다(위조 불가).
 */

let lastPath: string | null = null;

/** fire-and-forget. 측정 실패가 화면을 깨뜨리면 안 된다. */
export function trackPageView(path: string): void {
  // 비로그인 사용자는 기록 대상이 아니다 (서버도 401 로 거절).
  if (useAuthStore.getState().user == null) return;

  void apiClient.post('/page-views', { path } satisfies CreatePageView).catch(() => {
    // 계측 한 건은 버린다 — 화면이 이 실패를 알 이유가 없고, 운영 보고는 axios 인터셉터가 한다.
  });
}

interface PageViewRouter {
  subscribe: (event: 'onResolved', cb: () => void) => void;
  state: { matches: { routeId: string }[] };
}

/**
 * 라우터 네비게이션마다 현재 라우트를 기록한다.
 * 같은 라우트 연속 진입은 무시 — onResolved 는 search param 변경에도 발화한다.
 */
export function attachPageViewTracking(router: PageViewRouter): void {
  router.subscribe('onResolved', () => {
    const matches = router.state.matches;
    const routeId = matches[matches.length - 1]?.routeId;

    if (!routeId || routeId === lastPath) return;
    lastPath = routeId;
    trackPageView(routeId);
  });
}

/** 테스트 전용 — 모듈 레벨 dedupe 상태 초기화. */
export function resetPageViewTrackingForTest(): void {
  lastPath = null;
}

interface WindowViewStore {
  subscribe: (
    cb: (state: { openWindows: string[] }, prev: { openWindows: string[] }) => void
  ) => void;
}

/**
 * 창(window) 열림마다 `window:{id}` 경로로 기록한다.
 * 앱이 창 메타포라 화면 이동이 라우트를 안 바꿔 라우터 추적만으로는 기능 사용량이 안 보인다
 * (실측: 전체 page_views 고유 path 4개). openWindows 에 새로 추가된 id 만 기록 —
 * 재포커스·최소화 복원은 목록이 안 변하므로 자연히 제외된다.
 */
export function attachWindowViewTracking(store: WindowViewStore): void {
  store.subscribe((state, prev) => {
    for (const id of state.openWindows) {
      if (!prev.openWindows.includes(id)) trackPageView(`window:${id}`);
    }
  });
}
